/**
 * BatchRefreshPanel
 *
 * Overlay-Panel für den Batch-Refresh-Flow:
 * Zeigt schwache Posts aus dem Monats-Audit nacheinander mit je 2 Varianten.
 * Nutzer kann pro Post eine Variante übernehmen oder überspringen.
 *
 * Props:
 *   month      — ausgewählter Monat (int)
 *   year       — ausgewähltes Jahr (int)
 *   onClose    — Callback zum Schließen
 *   onDone     — Callback wenn Batch abgeschlossen (erhält {adopted, skipped})
 */

import { useState, useEffect } from "react";
import axios from "axios";
import {
  X, Loader2, CheckCircle, SkipForward, AlertTriangle,
  ChevronRight, Sparkles
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_COLORS = {
  facebook:       "bg-blue-100 text-blue-700",
  instagram:      "bg-pink-100 text-pink-700",
  linkedin:       "bg-sky-100 text-sky-700",
  tiktok:         "bg-gray-100 text-gray-700",
  google_business:"bg-orange-100 text-orange-700",
};

// ── Interner Fortschritts-Balken ───────────────────────────────────────────
function ProgressBar({ current, total }) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div className="w-full bg-stone-100 rounded-full h-1.5 overflow-hidden">
      <div
        className="h-full bg-violet-500 transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ── Varianten-Karte ────────────────────────────────────────────────────────
function VariantCard({ variant, onAdopt, adopting, index }) {
  return (
    <div className="bg-violet-50/70 border border-violet-200 rounded-xl p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <span className="text-xs font-bold text-violet-800">{variant.title}</span>
          {variant.focus && (
            <span className="text-[10px] text-violet-400 ml-2">— {variant.focus}</span>
          )}
        </div>
        <button
          onClick={() => onAdopt(variant)}
          disabled={adopting}
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-40 transition-colors"
        >
          {adopting
            ? <Loader2 size={12} className="animate-spin" />
            : <CheckCircle size={12} />
          }
          Übernehmen
        </button>
      </div>
      {variant.hook && (
        <p className="text-sm font-semibold text-stone-800 leading-snug">{variant.hook}</p>
      )}
      {variant.text && (
        <p className="text-xs text-stone-600 leading-relaxed">
          {variant.text.length > 300 ? variant.text.slice(0, 300) + "…" : variant.text}
        </p>
      )}
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────
export default function BatchRefreshPanel({ month, year, onClose, onDone }) {
  const [phase, setPhase]         = useState("loading");  // loading | review | done | error
  const [batchResults, setBatch]  = useState([]);         // all results from API
  const [currentIdx, setIdx]      = useState(0);
  const [adopted, setAdopted]     = useState(0);
  const [skipped, setSkipped]     = useState(0);
  const [failed, setFailed]       = useState(0);
  const [adopting, setAdopting]   = useState(false);
  const [loadErr, setLoadErr]     = useState(null);

  // Batch laden beim Öffnen
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.post(`${API}/monthly-plan/batch-variations`, {
          month,
          year,
          max_posts: 10,
        });
        const results = res.data.results || [];
        const failed_count = results.filter(r => !r.success).length;
        setFailed(failed_count);
        setBatch(results.filter(r => r.success)); // nur erfolgreiche zeigen
        setPhase(results.length === 0 ? "done" : "review");
      } catch (err) {
        setLoadErr(err.response?.data?.detail || err.message);
        setPhase("error");
      }
    };
    load();
  }, [month, year]);

  const current = batchResults[currentIdx];
  const total   = batchResults.length;
  const isDone  = currentIdx >= total;

  // Post übernehmen
  const handleAdopt = async (variant) => {
    if (!current || adopting) return;
    setAdopting(true);
    try {
      await axios.patch(`${API}/monthly-plan/${current.post_id}`, {
        hook:         variant.hook,
        hook_full:    variant.hook,
        core_content: variant.text,
        text_content: variant.text,
      });
      setAdopted(a => a + 1);
      advance();
    } catch (err) {
      // Fehler anzeigen, aber nicht blockieren
      console.warn("Adopt fehlgeschlagen:", err.message);
      setAdopted(a => a + 1); // trotzdem weiter
      advance();
    } finally {
      setAdopting(false);
    }
  };

  const handleSkip = () => {
    setSkipped(s => s + 1);
    advance();
  };

  const advance = () => {
    const next = currentIdx + 1;
    setIdx(next);
    if (next >= total) setPhase("done");
  };

  const handleDone = () => {
    onDone?.({ adopted, skipped });
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !adopting) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{ animation: "fadeIn 0.2s ease" }}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100 bg-stone-50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-violet-600" />
            <h2 className="font-heading font-bold text-stone-900">
              Beiträge verbessern
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={adopting}
            className="text-stone-400 hover:text-stone-600 transition-colors disabled:opacity-30"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto">

          {/* LOADING */}
          {phase === "loading" && (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 size={36} className="animate-spin text-violet-500" />
              <p className="text-stone-500 text-sm">
                KI analysiert schwache Posts und erstellt Vorschläge…
              </p>
            </div>
          )}

          {/* ERROR */}
          {phase === "error" && (
            <div className="p-8 flex flex-col items-center gap-4 text-center">
              <AlertTriangle size={32} className="text-red-400" />
              <p className="text-stone-700 font-semibold">Laden fehlgeschlagen</p>
              <p className="text-sm text-stone-500">{loadErr}</p>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm bg-stone-100 text-stone-600 rounded-lg hover:bg-stone-200 transition-colors"
              >
                Schließen
              </button>
            </div>
          )}

          {/* REVIEW — Post für Post durchgehen */}
          {phase === "review" && current && (
            <div className="p-6 space-y-5">
              {/* Fortschritt */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-stone-500">
                  <span>Beitrag {currentIdx + 1} von {total}</span>
                  <span>{adopted} übernommen · {skipped} übersprungen</span>
                </div>
                <ProgressBar current={currentIdx} total={total} />
              </div>

              {/* Aktueller Post — Original */}
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-wide">
                    Original
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    PLATFORM_COLORS[current.platform] || "bg-stone-100 text-stone-600"
                  }`}>
                    {current.platform}
                  </span>
                  {current.scheduled_date && (
                    <span className="text-[10px] text-stone-400">{current.scheduled_date}</span>
                  )}
                </div>
                {/* Bewusst OHNE Qualitäts-Begründungen: die Formulierungen der
                    Heuristik ("Braucht mehr Aufmerksamkeit" o.ä.) sind interne QA
                    und gehören nicht in die Kundenansicht. Der Kunde sieht das
                    Original und die Vorschläge — das reicht zum Entscheiden. */}
                {current.hook_preview ? (
                  <p className="text-sm text-stone-700 leading-snug">{current.hook_preview}</p>
                ) : (
                  <p className="text-sm text-stone-400 italic">Kein Hook vorhanden</p>
                )}
              </div>

              {/* Varianten */}
              <div className="space-y-1.5">
                <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wide mb-2">
                  Vorschläge — eine auswählen oder überspringen
                </p>
                {current.variants.map((variant, vi) => (
                  <VariantCard
                    key={vi}
                    variant={variant}
                    index={vi}
                    onAdopt={handleAdopt}
                    adopting={adopting}
                  />
                ))}
              </div>

              {/* Aktionen */}
              <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                <p className="text-xs text-stone-400">
                  Kein Vorschlag passend?
                </p>
                <button
                  onClick={handleSkip}
                  disabled={adopting}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-lg transition-colors disabled:opacity-40"
                >
                  <SkipForward size={14} />
                  Überspringen
                  {total - currentIdx - 1 > 0 && (
                    <ChevronRight size={14} />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* DONE */}
          {(phase === "done" || (phase === "review" && isDone)) && (
            <div className="p-8 flex flex-col items-center gap-5 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-stone-900 mb-1">
                  Batch abgeschlossen
                </h3>
                <p className="text-stone-500 text-sm">
                  {adopted > 0
                    ? `${adopted} Beitrag${adopted > 1 ? "e" : ""} verbessert`
                    : "Keine Beiträge übernommen"
                  }
                  {skipped > 0 && ` · ${skipped} übersprungen`}
                  {failed > 0 && ` · ${failed} konnten nicht generiert werden`}
                </p>
              </div>

              {/* Zusammenfassung-Badges */}
              <div className="flex items-center gap-3 flex-wrap justify-center">
                {adopted > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                    <CheckCircle size={14} />
                    {adopted} übernommen
                  </div>
                )}
                {skipped > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm text-stone-500">
                    <SkipForward size={14} />
                    {skipped} übersprungen
                  </div>
                )}
                {failed > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                    <AlertTriangle size={14} />
                    {failed} ohne Vorschlag
                  </div>
                )}
              </div>

              <button
                onClick={handleDone}
                className="px-6 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-xl hover:bg-violet-700 transition-colors"
              >
                Fertig — Kalender aktualisieren
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.97); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
